"use client";

// TODO: Re-enable when Convex backend is ready
// import { api } from "@/convex/_generated/api";
// import { useMutation } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { m } from "motion/react";
import { useDatafast } from "@henlo/analytics";
import { EnhancedLiveTicker } from "@/components/arcade/enhanced-live-ticker";
import { FloatingTexts } from "@/components/arcade/floating-texts";
import { MusicPlayer, type TetrisAudioState } from "@/components/arcade/music-player";
import { StatsSection } from "@/components/arcade/stats-section";
import { TVScreen } from "@/components/arcade/tv-screen";
// Extracted components
import { ArcadeSection } from "@/components/arcade-section";
import { PriceChartCard } from "@/components/price-chart-card";
import { TVOverlay } from "@/components/tv-overlay";
import { Button } from "@/components/ui/button";
// Hooks and utilities
import { getCategorizedApps, getGameApps, type GameApp } from "@/constants/apps";
import { ARCADE_STYLES } from "@/constants/arcade-styles";
import { useArcadeData, useArcadeState } from "@/hooks/arcade";
import { useSoundEffects } from "@thehoneyjar/sound-engine";
import { useTokenPrice } from "@/hooks/use-token-price";

// Comic book style status sticker component
const StatusSticker = ({ sticker }: { sticker: { text: string; color: string; rotate: number } | null }) => {
  if (!sticker) return null;

  return (
    <m.div
      className="absolute z-10 pointer-events-none"
      style={{
        top: "-8px",
        right: "-8px",
      }}
      initial={{ scale: 0, rotate: -45 }}
      animate={{ scale: 1, rotate: sticker.rotate }}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 15,
        delay: 0.2,
      }}
    >
      <m.div
        className="flex items-center justify-center rounded-sm"
        animate={{
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{
          background: sticker.color,
          width: "48px",
          height: "20px",
          boxShadow: `
            2px 2px 0 rgba(0,0,0,0.8),
            inset 0 1px 0 rgba(255,255,255,0.3),
            inset 0 -1px 0 rgba(0,0,0,0.3),
            0 0 10px rgba(0,0,0,0.3)
          `.trim(),
          border: "1px solid rgba(0,0,0,0.8)",
        }}
      >
        <span
          className="font-pressStart2P text-[6px] text-black font-bold"
          style={{
            textShadow: `
              1px 1px 0 rgba(255,255,255,0.3)
            `.trim(),
            letterSpacing: "-0.5px",
          }}
        >
          {sticker.text}
        </span>
        {/* Comic book dots */}
        <div
          className="absolute inset-0 opacity-10 rounded-sm pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, black 1px, transparent 1px)`,
            backgroundSize: "3px 3px",
          }}
        />
      </m.div>
    </m.div>
  );
};

const HomeArcade = () => {
  // State management using extracted hooks
  const {
    hoveredApp,
    floatingTexts,
    onHoverEnter,
    onHoverLeave,
    handleAppClick,
    addFloatingText,
    prefetchPath,
  } = useArcadeState();

  const { vaultApy, lockApy } = useArcadeData();
  const { playSound } = useSoundEffects();
  const { track } = useDatafast();
  const { data: tokenData } = useTokenPrice();

  const bearRef = useRef<HTMLDivElement | null>(null);

  // Audio state management
  const [tetrisAudioState, setTetrisAudioState] = useState<TetrisAudioState>({
    isPlaying: false,
    currentTrack: null,
    trackName: "",
  });
  const tetrisAudioControlRef = useRef<{
    play: () => void;
    pause: () => void;
  } | null>(null);

  const togglePlayPause = useCallback(() => {
    void playSound("buttonClick");
    if (tetrisAudioControlRef.current) {
      if (tetrisAudioState.isPlaying) {
        tetrisAudioControlRef.current.pause();
      } else {
        tetrisAudioControlRef.current.play();
      }
    }
  }, [tetrisAudioState.isPlaying, playSound]);

  const handleBearClick = useCallback(() => {
    // TODO(audio-register): consider isRouteHibernating gate — see constants/soundtrack.ts
    void playSound("henloVoice");

    const rect = bearRef.current?.getBoundingClientRect();
    if (!rect) return;

    addFloatingText(rect.left + rect.width / 2, rect.top);
  }, [playSound, addFloatingText]);

  const trackAppClick = useCallback(
    (app: GameApp, origin: string) => {
      track("arcade_app_clicked", {
        app_id: app.id,
        app_category: app.category,
        target_path: app.path,
        origin,
        is_locked: app.isLocked ? "1" : "0",
      });
    },
    [track],
  );

  const handleTrackedAppClick = useCallback(
    (app: GameApp, origin: string) => {
      trackAppClick(app, origin);
      handleAppClick(app);
    },
    [trackAppClick, handleAppClick],
  );

  // Stable per-section callbacks (prevents busting memo on ArcadeSection)
  const handlePlayClick = useCallback(
    (app: GameApp) => handleTrackedAppClick(app, "play_section"),
    [handleTrackedAppClick],
  );
  const handleCreateClick = useCallback(
    (app: GameApp) => handleTrackedAppClick(app, "create_section"),
    [handleTrackedAppClick],
  );

  const handleExternalBuyClick = useCallback(
    (app: GameApp, origin: string) => {
      trackAppClick(app, origin);
      track("arcade_buy_opened", {
        origin,
        app_id: app.id,
        target_path: app.path,
        target: "opensea",
      });
    },
    [track, trackAppClick],
  );

  // Format price for button display
  const formatButtonPrice = (price: number) => {
    if (price < 0.01) {
      return price.toFixed(6);
    } else if (price < 1) {
      return price.toFixed(5);
    }
    return price.toFixed(2);
  };

  // Get dynamic button text with price
  const getBuyButtonText = () => {
    if (!tokenData) return "BUY NOW";
    const price = formatButtonPrice(tokenData.price);
    return `BUY NOW • $${price}`;
  };

  // Get status sticker based on price change
  const getStatusSticker = () => {
    if (!tokenData) return null;

    const change = tokenData.change24h;
    if (Math.abs(change) < 0.5) {
      return { text: "FRESH", color: "#60A5FA", rotate: -5 }; // Light blue
    } else if (change > 0) {
      return { text: "HOT!", color: "#FB923C", rotate: 8 }; // Orange
    } else {
      return { text: "SALE!", color: "#FDE047", rotate: -3 }; // Bright yellow
    }
  };

  const statusSticker = getStatusSticker();

  // Get game apps with dynamic APY values (memoized to prevent cascading re-renders)
  const gameApps = useMemo(() => getGameApps(vaultApy, lockApy), [vaultApy, lockApy]);
  const { play: playApps, buy: buyApps, create: filteredCreateApps } = useMemo(
    () => getCategorizedApps(gameApps),
    [gameApps],
  );

  // Build a quick map for id -> path so we can prefetch on hover
  const appPathById = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of [playApps, buyApps, filteredCreateApps]) {
      for (const a of list) map.set(a.id, a.path);
    }
    return map;
  }, [playApps, buyApps, filteredCreateApps]);

  const onHoverEnterPrefetch = useCallback(
    (id: string) => {
      onHoverEnter(id);
      const path = appPathById.get(id);
      if (path) prefetchPath(path);
    },
    [onHoverEnter, appPathById, prefetchPath],
  );

  // Prefetch likely routes during idle time on good networks only
  useEffect(() => {
    const all = [playApps, filteredCreateApps, buyApps].flat();
    const internalPaths = Array.from(
      new Set(all.map((a) => a.path).filter((p) => p?.startsWith("/") && p !== "/buy")),
    );

    const nav: any = typeof navigator !== "undefined" ? navigator : undefined;
    const conn = nav?.connection || nav?.mozConnection || nav?.webkitConnection;
    const isSlow = !!conn && (conn.saveData || ["slow-2g", "2g", "3g"].includes(conn.effectiveType));
    if (isSlow) return;

    const idleId: any = (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(() => {
          internalPaths.forEach((p) => prefetchPath(p));
        }, { timeout: 3000 })
      : setTimeout(() => internalPaths.forEach((p) => prefetchPath(p)), 2500);

    return () => {
      if ((window as any).cancelIdleCallback) {
        (window as any).cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, [playApps, filteredCreateApps, buyApps, prefetchPath]);

  return (
    <>
      <div className="from-green-900 via-green-800 to-green-900 relative min-h-screen overflow-hidden bg-gradient-to-br pt-20">
        {/* Animated background rays */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30" style={ARCADE_STYLES.BACKGROUND_RAYS} />
        </div>

        {/* Main container */}
        <div className="relative z-10 mx-auto mt-16 max-w-7xl p-4">
          {/* Mobile layout */}
          <div className="md:hidden space-y-4">
            {/* Live Banner (no Tetris) */}
            <div className="relative overflow-hidden rounded-md p-2" style={ARCADE_STYLES.TV_FRAME}>
              <div
                className="relative flex h-10 items-center overflow-hidden rounded-md"
                style={ARCADE_STYLES.TICKER_CONTAINER}
              >
                <TVOverlay intensity="light" />
                <div
                  className="absolute left-0 z-30 flex h-full items-center gap-1.5 px-4"
                  style={ARCADE_STYLES.LIVE_INDICATOR.left}
                >
                  <div className="bg-red-500 shadow-red-500/50 size-2 animate-pulse rounded-full shadow-lg" />
                  <span className="font-pressStart2P text-[8px] uppercase text-white drop-shadow-[0_2px_4px_rgba(255,0,0,0.8)]">
                    LIVE
                  </span>
                </div>
                <div
                  className="absolute right-0 z-30 h-full"
                  style={ARCADE_STYLES.LIVE_INDICATOR.right}
                />
                {/* Live ticker content */}
                <EnhancedLiveTicker speed={35} />
              </div>
            </div>

            {/* Stats Section (moved above Buy) */}
            <StatsSection />

            {/* Buy Section */}
            <div>
              <div
                className="relative overflow-hidden rounded-t-md"
                style={ARCADE_STYLES.SECTION_HEADER}
              >
                <div className="px-3 py-1">
                  {/* Henlo TV header — moondust zinc + dying flicker.
                      The TV inside is off-air (CH 404 NO SIGNAL), so the
                      label flickers dying alongside the dead channel. */}
                  <h2 className="kansei-flicker-b font-pressStart2P text-[8px] uppercase tracking-[0.18em] text-zinc-500">
                    Henlo TV
                  </h2>
                </div>
              </div>
              <div className="relative rounded-b-md p-2" style={ARCADE_STYLES.SECTION_BODY}>
                <div className="space-y-2">
                  <PriceChartCard />
                  {/* COMMENTED OUT BUY BUTTON FOR TESTING
                  {buyApps.map((app) =>
                    // Special-case BUY on mobile: open external marketplace
                    app.id === "buy" ? (
                      <div key={app.id} className="relative">
                        <Button
                          asChild
                          variant="action-blue"
                          className="h-20 w-full text-[10px]"
                          onClick={() => handleExternalBuyClick(app, "buy_section")}
                          onMouseEnter={() => onHoverEnterPrefetch(app.id)}
                          onMouseLeave={onHoverLeave}
                        >
                          <a
                            href="https://opensea.io/token/bera_chain/0xb2f776e9c1c926c4b2e54182fac058da9af0b6a5"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span>{getBuyButtonText()}</span>
                              {app.subtitle ? (
                                <span className="text-[8px] text-muted-foreground">
                                  {app.subtitle}
                                </span>
                              ) : null}
                            </div>
                          </a>
                        </Button>
                        <StatusSticker sticker={statusSticker} />
                      </div>
                    ) : (
                      <Button
                        key={app.id}
                        variant="action-blue"
                        className="h-20 w-full text-[10px]"
                        onClick={() => handleTrackedAppClick(app, "buy_section")}
                        onMouseEnter={() => onHoverEnterPrefetch(app.id)}
                        onMouseLeave={onHoverLeave}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span>{app.title}</span>
                          {app.subtitle ? (
                            <span className="text-[8px] text-muted-foreground">{app.subtitle}</span>
                          ) : null}
                        </div>
                      </Button>
                    ),
                  )}
                  */}
                </div>
              </div>
            </div>

            {/* Play & Create */}
            <ArcadeSection
              title="Play"
              titleColor="text-yellow-300"
              apps={playApps}
              hoveredApp={hoveredApp}
              onAppClick={handlePlayClick}
              onHoverEnter={onHoverEnterPrefetch}
              onHoverLeave={onHoverLeave}
            />
            <ArcadeSection
              title="Create"
              titleColor="text-purple-300"
              apps={filteredCreateApps}
              hoveredApp={hoveredApp}
              onAppClick={handleCreateClick}
              onHoverEnter={onHoverEnterPrefetch}
              onHoverLeave={onHoverLeave}
            />

            {/* Music at bottom */}
            <MusicPlayer
              audioState={tetrisAudioState}
              onPlayPause={togglePlayPause}
              onBearClick={handleBearClick}
            />
          </div>

          {/* Desktop layout */}
          <div className="relative hidden md:block">
            {/* Main Layout Grid - Three Distinct Sections */}
            <div className="grid grid-cols-12 gap-4">
              {/* Left Section - Play & Create */}
              <div className="col-span-3 space-y-4">
                {/* Play Section */}
                <ArcadeSection
                  title="Play"
                  titleColor="text-yellow-300"
                  apps={playApps}
                  hoveredApp={hoveredApp}
                  onAppClick={handlePlayClick}
                  onHoverEnter={onHoverEnter}
                  onHoverLeave={onHoverLeave}
                />

                {/* Create Section */}
                <ArcadeSection
                  title="Create"
                  titleColor="text-purple-300"
                  apps={filteredCreateApps}
                  hoveredApp={hoveredApp}
                  onAppClick={handleCreateClick}
                  onHoverEnter={onHoverEnter}
                  onHoverLeave={onHoverLeave}
                />
              </div>

              {/* Center Section - TV Screen and Music Player */}
              <div className="col-span-6 space-y-2">
                <TVScreen
                  onAudioStateChange={setTetrisAudioState}
                  audioControlRef={tetrisAudioControlRef}
                />
                {/* Music Player - positioned below TV Screen */}
                <MusicPlayer
                  audioState={tetrisAudioState}
                  onPlayPause={togglePlayPause}
                  onBearClick={handleBearClick}
                />
              </div>

              {/* Right Section - Stats & Henlo TV */}
              <div className="col-span-3 space-y-4">
                {/* Stats Section */}
                <StatsSection />

                {/* Buy Section */}
                <div>
                  <div
                    className="relative overflow-hidden rounded-t-md"
                    style={ARCADE_STYLES.SECTION_HEADER}
                  >
                    <div className="px-3 py-1">
                      {/* Henlo TV header — moondust zinc + dying flicker.
                      The TV inside is off-air (CH 404 NO SIGNAL), so the
                      label flickers dying alongside the dead channel. */}
                  <h2 className="kansei-flicker-b font-pressStart2P text-[8px] uppercase tracking-[0.18em] text-zinc-500">
                    Henlo TV
                  </h2>
                    </div>
                  </div>
                  <div className="relative rounded-b-md p-2" style={ARCADE_STYLES.SECTION_BODY}>
                    <div className="space-y-2">
                      {/* Price Chart Card */}
                      <PriceChartCard />

                      {/* COMMENTED OUT BUY BUTTON FOR TESTING
                      {buyApps.map((app) =>
                        app.id === "buy" ? (
                          <div key={app.id} className="relative">
                            <Button
                              asChild
                              variant="action-blue"
                              className="h-20 w-full text-[10px]"
                              onClick={() => handleExternalBuyClick(app, "buy_section")}
                              onMouseEnter={() => onHoverEnter(app.id)}
                              onMouseLeave={onHoverLeave}
                            >
                              <a
                                href="https://opensea.io/token/bera_chain/0xb2f776e9c1c926c4b2e54182fac058da9af0b6a5"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <span>{getBuyButtonText()}</span>
                                  {app.subtitle ? (
                                    <span className="text-[8px] text-muted-foreground">
                                      {app.subtitle}
                                    </span>
                                  ) : null}
                                </div>
                              </a>
                            </Button>
                            <StatusSticker sticker={statusSticker} />
                          </div>
                        ) : (
                          <Button
                            key={app.id}
                            variant="action-blue"
                            className="h-20 w-full text-[10px]"
                            onClick={() => handleTrackedAppClick(app, "buy_section")}
                            onMouseEnter={() => onHoverEnterPrefetch(app.id)}
                            onMouseLeave={onHoverLeave}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span>{app.title}</span>
                              {app.subtitle ? (
                                <span className="text-[8px] text-muted-foreground">
                                  {app.subtitle}
                                </span>
                              ) : null}
                            </div>
                          </Button>
                        ),
                      )}
                      */}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <style>{`
          @keyframes rays {
            0% {
              transform: translateX(0) translateY(0);
            }
            100% {
              transform: translateX(40px) translateY(40px);
            }
          }

          @keyframes float {
            0%,
            100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-20px);
            }
          }

          @keyframes barScale {
            0%,
            100% {
              transform: scaleY(0.3);
            }
            20% {
              transform: scaleY(0.6);
            }
            40% {
              transform: scaleY(1);
            }
            60% {
              transform: scaleY(0.8);
            }
            80% {
              transform: scaleY(0.5);
            }
          }
        `}</style>
      </div>

      {/* Floating "henlo" texts */}
      <FloatingTexts texts={floatingTexts} />

    </>
  );
};

export default HomeArcade;
